import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAddress, zeroAddress, type Address, type PublicClient } from 'viem';
import { usePublicClient, useWatchContractEvent } from 'wagmi';
import { FACTORY_ABI, PAIR_ABI } from './abis';
import { getDeployment, type OnchainPair } from './contracts';

const MULTICALL_CHUNK_SIZE = 100;
const pairCache = new Map<string, readonly OnchainPair[]>();
const discoveryRequests = new Map<string, Promise<readonly OnchainPair[]>>();

function cacheKey(chainId: number, factory: Address): string {
  return `${chainId}:${factory.toLowerCase()}`;
}

function requireSuccessfulResult<T>(
  result: { status: 'success'; result: unknown } | { status: 'failure'; error: Error },
  label: string,
): T {
  if (result.status === 'failure') throw new Error(`${label}: ${result.error.message}`);
  return result.result as T;
}

async function discoverFactoryPairs(
  client: PublicClient,
  chainId: number,
  factory: Address,
): Promise<readonly OnchainPair[]> {
  const key = cacheKey(chainId, factory);
  const cached = pairCache.get(key);
  if (cached) return cached;

  const pending = discoveryRequests.get(key);
  if (pending) return pending;

  const request = (async () => {
    const blockNumber = await client.getBlockNumber();
    const pairCount = await client.readContract({
      address: factory,
      abi: FACTORY_ABI,
      functionName: 'allPairsLength',
      blockNumber,
    });
    if (pairCount > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new Error(`Factory pair count is too large to enumerate safely: ${pairCount}`);
    }

    const pairAddresses: Address[] = [];
    const count = Number(pairCount);
    for (let start = 0; start < count; start += MULTICALL_CHUNK_SIZE) {
      const end = Math.min(start + MULTICALL_CHUNK_SIZE, count);
      const results = await client.multicall({
        allowFailure: true,
        blockNumber,
        contracts: Array.from({ length: end - start }, (_, offset) => ({
          address: factory,
          abi: FACTORY_ABI,
          functionName: 'allPairs' as const,
          args: [BigInt(start + offset)],
        })),
      });
      results.forEach((result, offset) => {
        const pair = requireSuccessfulResult<Address>(result, `allPairs(${start + offset})`);
        if (pair === zeroAddress) throw new Error(`allPairs(${start + offset}) returned the zero address`);
        pairAddresses.push(getAddress(pair));
      });
    }

    const discovered: OnchainPair[] = [];
    for (let start = 0; start < pairAddresses.length; start += MULTICALL_CHUNK_SIZE) {
      const addresses = pairAddresses.slice(start, start + MULTICALL_CHUNK_SIZE);
      const results = await client.multicall({
        allowFailure: true,
        blockNumber,
        contracts: addresses.flatMap((address) => [
          { address, abi: PAIR_ABI, functionName: 'token0' as const },
          { address, abi: PAIR_ABI, functionName: 'token1' as const },
        ]),
      });
      addresses.forEach((address, index) => {
        const token0 = requireSuccessfulResult<Address>(results[index * 2], `${address}.token0()`);
        const token1 = requireSuccessfulResult<Address>(results[index * 2 + 1], `${address}.token1()`);
        discovered.push({ address, token0: getAddress(token0), token1: getAddress(token1) });
      });
    }

    const verified: OnchainPair[] = [];
    for (let start = 0; start < discovered.length; start += MULTICALL_CHUNK_SIZE) {
      const pairs = discovered.slice(start, start + MULTICALL_CHUNK_SIZE);
      const results = await client.multicall({
        allowFailure: true,
        blockNumber,
        contracts: pairs.map((pair) => ({
          address: factory,
          abi: FACTORY_ABI,
          functionName: 'getPair' as const,
          args: [pair.token0, pair.token1],
        })),
      });
      pairs.forEach((pair, index) => {
        const registered = requireSuccessfulResult<Address>(results[index], `getPair(${pair.token0}, ${pair.token1})`);
        if (registered.toLowerCase() === pair.address.toLowerCase()) verified.push(pair);
      });
    }

    pairCache.set(key, verified);
    return verified;
  })();

  discoveryRequests.set(key, request);
  try {
    return await request;
  } finally {
    discoveryRequests.delete(key);
  }
}

export interface FactoryPairsState {
  pairs: readonly OnchainPair[];
  loading: boolean;
  error: Error | null;
  unsupportedChain: boolean;
  refresh: () => void;
}

export function useFactoryPairs(chainId: number): FactoryPairsState {
  const deployment = getDeployment(chainId);
  const factory = deployment?.factory;
  const key = useMemo(() => (factory ? cacheKey(chainId, factory) : null), [chainId, factory]);
  const client = usePublicClient({ chainId });
  const [pairs, setPairs] = useState<readonly OnchainPair[]>(() => (key ? pairCache.get(key) ?? [] : []));
  const [loading, setLoading] = useState(Boolean(factory && !pairCache.has(key ?? '')));
  const [error, setError] = useState<Error | null>(null);
  const [revision, setRevision] = useState(0);
  const unsupportedChain = !deployment;

  const refresh = useCallback(() => {
    if (key) pairCache.delete(key);
    setRevision((value) => value + 1);
  }, [key]);

  useWatchContractEvent({
    address: factory,
    abi: FACTORY_ABI,
    eventName: 'PairCreated',
    chainId,
    enabled: Boolean(factory),
    onLogs: refresh,
  });

  useEffect(() => {
    let cancelled = false;

    if (!factory || !client || !key) {
      setPairs([]);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    const cached = pairCache.get(key);
    if (cached) {
      setPairs(cached);
      setLoading(false);
      setError(null);
      return () => {
        cancelled = true;
      };
    }

    setPairs([]);
    setLoading(true);
    setError(null);
    discoverFactoryPairs(client, chainId, factory)
      .then((discovered) => {
        if (!cancelled) setPairs(discovered);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [chainId, client, factory, key, revision]);

  return { pairs, loading, error, unsupportedChain, refresh };
}
